import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { billingService } from "../services/billing";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import AppShell from "../components/layout/AppShell";
import { requestBodyScrollLock } from "../utils/bodyScrollLock";

const PLAN_DESCRIPTIONS = {
  free: {
    price: "$0/mo",
    subtitle: "Essential tools to test the confident rebel outbound methodology.",
    features: ["5 Leads Viewed / month", "Basic Email Composition"],
  },
  growth: {
    price: "$1.50/lead",
    subtitle: "Perfect for trying out reverse pitching.",
    features: ["Pay as you go", "Basic ICP matching", "Gmail integration"],
  },
  power: {
    price: "$99/mo",
    subtitle: "Unrestricted access for teams ready to flip the script on cold outreach.",
    features: [
      "Unlimited Leads",
      "Priority AI processing",
      "Multi-inbox support",
      "Advanced Analytics Dashboard",
    ],
  },
};

const PlanCard = ({ planKey, currentPlan, onOpenUpgrade, recommended = false }) => {
  const plan = PLAN_DESCRIPTIONS[planKey];
  const isCurrent = currentPlan === planKey;
  return (
    <Card className={`min-h-[360px] ${recommended ? "border-theme-accent shadow-accent-glow" : ""}`}>
      <div className="flex items-center justify-between">
        <h3 className={`font-display font-bold text-[32px] leading-tight ${planKey === "power" ? "text-theme-accent" : "text-theme-on-background"}`}>
          {planKey.charAt(0).toUpperCase() + planKey.slice(1)}
        </h3>
        {recommended ? (
          <span className="rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.1em] bg-theme-accent text-[#07090C]">
            Recommended
          </span>
        ) : null}
      </div>
      <p className="mt-1 text-[36px] font-bold text-theme-on-background">{plan.price}</p>
      <p className="mt-3 font-mono text-[11px] text-theme-muted">{plan.subtitle}</p>
      <div className="mt-5 h-px bg-theme-border" />
      <ul className="mt-5 space-y-2">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-center gap-3 font-mono text-[12px] text-theme-muted">
            <span className="material-symbols-outlined text-theme-accent text-[16px]">check_circle</span>
            {feature}
          </li>
        ))}
      </ul>
      <div className="mt-8">
        {isCurrent ? (
          <button
            type="button"
            disabled
            className="w-full rounded border border-theme-border py-2.5 font-mono text-[11px] uppercase tracking-[0.1em] text-theme-dim"
          >
            Current Plan
          </button>
        ) : (
          <Button
            variant={planKey === "power" ? "primary" : "ghost"}
            onClick={() => onOpenUpgrade(planKey)}
            className="w-full justify-center"
          >
            {planKey === "power" ? "Upgrade to Power" : "Get Started"}
          </Button>
        )}
      </div>
    </Card>
  );
};

const UpgradeModal = ({ open, selectedPlan, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    return requestBodyScrollLock();
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center px-6" role="dialog" aria-modal="true">
      <Card className="w-full max-w-lg">
        <h2 className="font-display font-bold text-2xl text-theme-on-background">
          Upgrade to {selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)}
        </h2>
        <p className="mt-3 font-mono text-[12px] text-theme-muted">
          Billing is not available for your user yet. Please email Fed to get set up if you want a paid plan.
        </p>
        <div className="mt-8 flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={onClose}>Continue</Button>
        </div>
      </Card>
    </div>
  );
};

const Billing = (props) => {
  const [selectedPlan, setSelectedPlan] = useState("power");
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ["billing"],
    queryFn: billingService.getSummary,
  });

  const usagePercent = data?.usage?.usage_percent || 0;
  const currentPlan = data?.current_plan || "free";
  const invoices = data?.invoices || [];
  const paymentMethod = data?.payment_method || {};

  const openUpgrade = (planKey) => {
    setSelectedPlan(planKey);
    setUpgradeOpen(true);
  };

  return (
    <AppShell
      currentUser={props.currentUser}
      pageTitle="Billing"
      pageSubtitle="Plan and usage"
    >
      <div className="space-y-8">
        <div className="hidden md:block">
          <h1 className="font-display font-bold italic text-[40px] leading-[1.1] text-theme-on-background">Billing</h1>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <Card className="xl:col-span-2">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-2xl text-theme-on-background">
                {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} Plan
              </h2>
              <span className="rounded border border-theme-border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.1em] text-theme-dim">
                Active
              </span>
            </div>
            <p className="mt-3 font-mono text-[12px] text-theme-muted">
              You're exploring the basics. Hit your limit and upgrade to remove friction and scale your outbound.
            </p>
            <div className="mt-8 flex items-end gap-2">
              <span className="text-5xl font-bold text-theme-on-background">{data?.usage?.used ?? 0}</span>
              <span className="text-2xl text-theme-dim">/ {data?.usage?.limit ?? 0}</span>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-theme-dim mt-1">Leads viewed this month</p>
            <div className="mt-3 h-1.5 rounded-full bg-theme-main border border-theme-border overflow-hidden">
              <div className="h-full bg-theme-accent transition-all" style={{ width: `${usagePercent}%` }} />
            </div>
            <p className="mt-2 font-mono text-[11px] text-rose-400">Limit reached, upgrades apply instantly.</p>
          </Card>

          <Card className="border-theme-accent shadow-accent-glow">
            <div className="material-symbols-outlined text-theme-accent text-3xl">bolt</div>
            <h2 className="mt-4 font-display font-bold text-xl text-theme-on-background">Ready to scale?</h2>
            <p className="mt-3 font-mono text-[12px] text-theme-muted">
              Unlock unlimited leads and priority processing to dominate your outbound strategy.
            </p>
            <Button className="mt-6 w-full justify-center" onClick={() => openUpgrade("power")}>
              Upgrade to Power
              <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
            </Button>
          </Card>
        </div>

        <section>
          <h2 className="font-display font-bold text-xl text-theme-on-background mb-4">Plan Comparison</h2>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <PlanCard planKey="free" currentPlan={currentPlan} onOpenUpgrade={openUpgrade} />
            <PlanCard planKey="growth" currentPlan={currentPlan} onOpenUpgrade={openUpgrade} />
            <PlanCard planKey="power" currentPlan={currentPlan} onOpenUpgrade={openUpgrade} recommended />
          </div>
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <Card>
            <h3 className="font-display font-bold text-lg text-theme-on-background">Invoice History</h3>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-theme-border">
                    <th className="pb-3 pr-4 font-mono text-[10px] uppercase tracking-[0.1em] text-theme-dim">Date</th>
                    <th className="pb-3 pr-4 font-mono text-[10px] uppercase tracking-[0.1em] text-theme-dim">Invoice ID</th>
                    <th className="pb-3 pr-4 font-mono text-[10px] uppercase tracking-[0.1em] text-theme-dim">Amount</th>
                    <th className="pb-3 font-mono text-[10px] uppercase tracking-[0.1em] text-theme-dim">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.length > 0 ? (
                    invoices.map((invoice) => (
                      <tr key={invoice.id} className="border-b border-theme-border/40">
                        <td className="py-3 pr-4 font-mono text-[12px] text-theme-muted">{invoice.date}</td>
                        <td className="py-3 pr-4 font-mono text-[12px] text-theme-dim">{invoice.id}</td>
                        <td className="py-3 pr-4 font-mono text-[12px] text-theme-text">{invoice.amount}</td>
                        <td className="py-3 font-mono text-[12px] text-theme-accent">{invoice.status}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="py-6 font-mono text-[12px] text-theme-dim" colSpan={4}>No invoices yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
          <Card>
            <h3 className="font-display font-bold text-lg text-theme-on-background">Payment Method</h3>
            <p className="mt-3 font-mono text-[12px] text-theme-muted">
              {paymentMethod.brand ? `${paymentMethod.brand} ending in ${paymentMethod.last4}` : "No payment method on file."}
            </p>
            {paymentMethod.exp ? <p className="font-mono text-[12px] text-theme-dim">{`Expires ${paymentMethod.exp}`}</p> : null}
            <Button variant="ghost" className="mt-6">Manage payment method</Button>
          </Card>
        </section>

        <UpgradeModal open={upgradeOpen} selectedPlan={selectedPlan} onClose={() => setUpgradeOpen(false)} />
      </div>
    </AppShell>
  );
};

export default Billing;
